import { createApiRef } from '@backstage/core-plugin-api';
import type {
  PaginatedResult,
  IncidentId,
  ExecutionId,
  TicketId,
  Incident,
  IncidentFilter,
  ExecutionSummary,
  ExecutionDetail,
  ExecutionEvent,
  SLODefinition,
  SLODetail,
  Postmortem,
  PostmortemFilter,
  Checklist,
  CheckItem,
  KaizenItem,
  MonthlyReport,
  PlatformRole,
  PlatformRoleName,
  UserRoleAssignment,
  AuditLog,
  AuditLogFilter,
  AuditExportFormat,
  ResourceExclusion,
  CreateExclusionRequest,
  ExclusionFilter,
  DecisionSearchFilter,
  ChangeSearchFilter,
  DecisionLog,
  ChangeLog,
  SimilarDecision,
  TimelineEntry,
  Notification,
  ActivityFeedItem,
  Ticket,
  TicketFilter,
  CreateTicketRequest,
  UpdateTicketRequest,
} from '../types';

/**
 * SRE Dashboard API Interface
 *
 * Defines the contract for communicating with the superagent-sre backend
 * through the Backstage proxy layer. 25 methods covering all SRE domains.
 */
export interface SreDashboardApi {
  // ── Incidents ──────────────────────────────────────────────
  getIncidents(filter?: IncidentFilter): Promise<PaginatedResult<Incident>>;
  getIncident(id: IncidentId): Promise<Incident | null>;

  // ── Runbook Execution ──────────────────────────────────────
  startRunbook(incidentId: IncidentId): Promise<ExecutionSummary>;
  getExecution(execId: ExecutionId): Promise<ExecutionDetail>;
  approveStep(execId: ExecutionId, stepName: string): Promise<void>;
  denyStep(execId: ExecutionId, stepName: string, reason: string): Promise<void>;
  streamExecution(execId: ExecutionId, onEvent: (event: ExecutionEvent) => void): () => void;

  // ── SLO ────────────────────────────────────────────────────
  getSLOs(): Promise<readonly SLODefinition[]>;
  getSLODetail(sloId: string): Promise<SLODetail | null>;

  // ── Postmortem ─────────────────────────────────────────────
  getPostmortems(filter?: PostmortemFilter): Promise<PaginatedResult<Postmortem>>;
  createPostmortem(incidentId: IncidentId): Promise<Postmortem>;

  // ── DailyOps ───────────────────────────────────────────────
  getChecklist(date: string): Promise<Checklist>;
  toggleCheckItem(itemId: string): Promise<CheckItem>;
  getKaizenItems(): Promise<readonly KaizenItem[]>;
  getMonthlyReport(month: string): Promise<MonthlyReport | null>;

  // ── Governance ─────────────────────────────────────────────
  getRoles(): Promise<readonly PlatformRole[]>;
  getAssignments(): Promise<readonly UserRoleAssignment[]>;
  assignRole(userId: string, roleName: PlatformRoleName): Promise<UserRoleAssignment>;
  revokeRole(assignmentId: string): Promise<void>;
  getAuditLogs(filter?: AuditLogFilter): Promise<PaginatedResult<AuditLog>>;
  exportAuditLogs(filter: AuditLogFilter, format: AuditExportFormat): Promise<string>;

  // ── Exclusions ─────────────────────────────────────────────
  getExclusions(filter?: ExclusionFilter): Promise<PaginatedResult<ResourceExclusion>>;
  createExclusion(req: CreateExclusionRequest): Promise<ResourceExclusion>;
  deleteExclusion(id: string): Promise<void>;

  // ── History ────────────────────────────────────────────────
  searchDecisions(filter: DecisionSearchFilter): Promise<PaginatedResult<DecisionLog>>;
  searchChanges(filter: ChangeSearchFilter): Promise<PaginatedResult<ChangeLog>>;
  findSimilarDecisions(incidentId: IncidentId): Promise<readonly SimilarDecision[]>;
  getTimeline(incidentId: IncidentId): Promise<readonly TimelineEntry[]>;

  // ── Tickets ──────────────────────────────────────────────────
  getTickets(filter?: TicketFilter): Promise<PaginatedResult<Ticket>>;
  getTicket(id: TicketId): Promise<Ticket | null>;
  createTicket(req: CreateTicketRequest): Promise<Ticket>;
  updateTicket(id: TicketId, req: UpdateTicketRequest): Promise<Ticket>;
  addTicketComment(id: TicketId, content: string): Promise<Ticket>;

  // ── Notifications ─────────────────────────────────────────
  getNotifications(): Promise<readonly Notification[]>;
  getActivityFeed(): Promise<readonly ActivityFeedItem[]>;
}

/**
 * API Reference for dependency injection via Backstage's useApi() hook.
 */
export const sreDashboardApiRef = createApiRef<SreDashboardApi>({
  id: 'plugin.sre-dashboard.api',
});
